import React from 'react';
import { Col, Divider, Row } from 'antd';

import {
  MemberHistoryDetailsModel,
  ProfileCorporatePrimaryContactInfoModel,
} from 'api/adminApiClient';
import HistoryLog from '../../HistoryLog';
import HistoryField from '../../HistoryField';
import {
  getPrimaryContactsMaxLength,
  hasAnyPrimaryContactsDisplayableChange,
  hasPrimaryContactFieldChanged,
} from 'utils/primaryContactHistoryUtils';
import {
  getContactForLabel,
  getPreferredContactMethodLabel,
} from 'utils/primaryContactUtils';

import styles from '../../HistoryCard.module.less';

type Props = {
  historyDetails: MemberHistoryDetailsModel;
  historyId: string;
};

export default function PrimaryContactInformationDetailsCard(props: Props) {
  const { historyDetails, historyId } = props;
  const { oldData, newData } = historyDetails;
  const oldPrimaryContacts = oldData?.corporateInfo?.primaryContacts;
  const newPrimaryContacts = newData?.corporateInfo?.primaryContacts;

  const hasPrimaryContactsDisplayableChange =
    hasAnyPrimaryContactsDisplayableChange(oldPrimaryContacts, newPrimaryContacts);

  const renderPrimaryContacts = (
    primaryContacts?: ProfileCorporatePrimaryContactInfoModel[],
  ) => {
    if (!hasPrimaryContactsDisplayableChange) {
      return null;
    }

    const sectionName = 'primary-contact';
    const maxLength = getPrimaryContactsMaxLength(
      oldPrimaryContacts,
      newPrimaryContacts,
    );

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Primary contact</div>
          </Col>

          <Col xs={24}>
            {Array.from({ length: maxLength }, (_, index) => {
              const primaryContact = primaryContacts?.[index];
              const showContactBlock =
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'firstName',
                ) ||
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'lastName',
                ) ||
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'email',
                ) ||
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'phoneNumber',
                ) ||
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'title',
                ) ||
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'preferredContactMethod',
                ) ||
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'contactFor',
                );

              if (!showContactBlock) {
                return null;
              }

              return (
                <React.Fragment key={`${primaryContact?.contactId ?? index}-${index}`}>
                  <Row gutter={[10, 10]}>
                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'firstName',
                    ) && (
                      <HistoryField
                        title="First name"
                        value={primaryContact?.firstName}
                        dataId={`${sectionName}-first-name-field-${index}`}
                      />
                    )}
                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'lastName',
                    ) && (
                      <HistoryField
                        title="Last name"
                        value={primaryContact?.lastName}
                        dataId={`${sectionName}-last-name-field-${index}`}
                      />
                    )}
                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'email',
                    ) && (
                      <HistoryField
                        title="Email"
                        value={primaryContact?.email}
                        dataId={`${sectionName}-email-field-${index}`}
                      />
                    )}
                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'phoneNumber',
                    ) && (
                      <HistoryField
                        title="Phone number"
                        value={primaryContact?.phoneNumber}
                        dataId={`${sectionName}-phone-number-field-${index}`}
                      />
                    )}
                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'title',
                    ) && (
                      <HistoryField
                        title="Title"
                        value={primaryContact?.title}
                        dataId={`${sectionName}-title-field-${index}`}
                      />
                    )}
                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'preferredContactMethod',
                    ) && (
                      <HistoryField
                        title="Preferred Contact Method"
                        value={getPreferredContactMethodLabel(
                          primaryContact?.preferredContactMethod,
                        )}
                        dataId={`${sectionName}-preferred-contact-method-field-${index}`}
                      />
                    )}
                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'contactFor',
                    ) && (
                      <HistoryField
                        title="Contact For"
                        value={getContactForLabel(primaryContact?.contactFor)}
                        dataId={`${sectionName}-contact-for-field-${index}`}
                      />
                    )}
                  </Row>

                  <Divider />
                </React.Fragment>
              );
            })}
          </Col>
        </Row>

        <Divider />
      </>
    );
  };

  if (!hasPrimaryContactsDisplayableChange) {
    return null;
  }

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderPrimaryContacts(oldPrimaryContacts)}
      newData={renderPrimaryContacts(newPrimaryContacts)}
    />
  );
}
